'use client';

import {
  type ApiErrorPayload,
  parseApiError,
} from '@/ai/jobs/client/api-error';
import {
  shouldContinueEnhancerJobPolling,
  shouldTimeoutEnhancerJobOnClient,
} from '@/ai/jobs/client/job-client-timeout';
import {
  MAX_POLL_ATTEMPTS,
  resolvePollDelayMs,
} from '@/ai/jobs/client/polling';
import type { ApiAiJob } from '@/ai/jobs/client/types';
import { AI_JOB_COMMAND_ERROR_CODES } from '@/ai/jobs/domain/job-command-error-contract';
import { isMediaAssetDerivativeComplete } from '@/ai/media/domain/media-asset';
import {
  isMediaAssetDerivativePolicyCurrent,
  requiresMediaAssetDerivativeProcessing,
} from '@/ai/media/domain/media-derivative-policy';
import { DATABASE_UNAVAILABLE_ERROR_CODE } from '@/lib/runtime/database-error-contract';
import {
  fetchCommandOnce,
  fetchPureReadWithOneRetry,
  isRetryablePureReadResponseStatus,
  isTransientNetworkRequestError,
} from '@/lib/runtime/fetch-with-retry';
import type { WorkspaceTurn, WorkspaceTurnsPage } from '@/workspace/media-turn';

const CREATE_JOB_TIMEOUT_MS = 20_000;
const CREATE_JOB_RECOVERY_WINDOW_MS = 30_000;
const GET_JOB_TIMEOUT_MS = 10_000;
const TIMEOUT_JOB_TIMEOUT_MS = 15_000;
const SECURITY_CHALLENGE_REQUIRED_CODE = 'SECURITY_CHALLENGE_REQUIRED';
const SECURITY_GOOGLE_LOGIN_REQUIRED_CODE = 'SECURITY_GOOGLE_LOGIN_REQUIRED';
const SECURITY_TEMP_BLOCKED_CODE = 'SECURITY_TEMP_BLOCKED';
const MEDIA_DERIVATIVE_SETTLE_FAST_ATTEMPTS = 72;
const MEDIA_DERIVATIVE_SETTLE_MAX_DELAY_MS = 10_000;
const MEDIA_DERIVATIVE_BACKGROUND_POLL_DELAY_MS = 60_000;
const MEDIA_DERIVATIVE_BACKGROUND_POLL_ATTEMPTS = 180;
const MEDIA_DERIVATIVE_SETTLE_MAX_ATTEMPTS =
  MEDIA_DERIVATIVE_SETTLE_FAST_ATTEMPTS +
  MEDIA_DERIVATIVE_BACKGROUND_POLL_ATTEMPTS;

function resolveMediaDerivativeSettleDelayMs(attempt: number) {
  if (attempt >= MEDIA_DERIVATIVE_SETTLE_FAST_ATTEMPTS) {
    return MEDIA_DERIVATIVE_BACKGROUND_POLL_DELAY_MS;
  }
  return Math.min(
    1_500 * 2 ** Math.min(attempt, 3),
    MEDIA_DERIVATIVE_SETTLE_MAX_DELAY_MS
  );
}

export type WorkspaceCreateJobPayload = {
  sourceJobId?: string;
  toolId: string;
  toolVersionToken: string;
  clientRequestId: string;
  captchaToken?: string | null;
  generationMode?: 'single' | 'batch';
  input: Record<string, unknown>;
  clientImageMeta?: Record<string, unknown>;
  clientMediaMeta?: Record<string, unknown>;
  originalImageName: string;
};

export type WorkspaceRepeatCreateJobPayload = Omit<
  WorkspaceCreateJobPayload,
  'clientRequestId' | 'generationMode'
> & {
  clientRequestId: string;
  clientRequestIds: string[];
};

export type WorkspaceRepeatCreateJobResult = {
  clientRequestId: string;
  items: Array<
    | {
        clientRequestId: string;
        job: ApiAiJob;
      }
    | {
        clientRequestId: string;
        status: number;
        error: {
          error: string;
          code?: string;
          details?: unknown;
          retryAfter?: number;
        };
      }
  >;
};

export class WorkspaceCreateChallengeError extends Error {
  details: unknown;
  invalidCaptcha: boolean;
  payload: WorkspaceCreateJobPayload;

  constructor(payload: WorkspaceCreateJobPayload, apiError: ApiErrorPayload) {
    super(apiError.message);
    this.name = 'WorkspaceCreateChallengeError';
    this.details = apiError.details;
    this.invalidCaptcha =
      typeof apiError.details === 'object' &&
      apiError.details !== null &&
      'invalidCaptcha' in apiError.details &&
      apiError.details.invalidCaptcha === true;
    this.payload = payload;
  }
}

export function isWorkspaceCreateChallengeError(
  error: unknown
): error is WorkspaceCreateChallengeError {
  return error instanceof WorkspaceCreateChallengeError;
}

export class WorkspaceRepeatCreateChallengeError extends Error {
  details: unknown;
  invalidCaptcha: boolean;
  payload: WorkspaceRepeatCreateJobPayload;

  constructor(
    payload: WorkspaceRepeatCreateJobPayload,
    apiError: ApiErrorPayload
  ) {
    super(apiError.message);
    this.name = 'WorkspaceRepeatCreateChallengeError';
    this.details = apiError.details;
    this.invalidCaptcha =
      typeof apiError.details === 'object' &&
      apiError.details !== null &&
      'invalidCaptcha' in apiError.details &&
      apiError.details.invalidCaptcha === true;
    this.payload = payload;
  }
}

export function isWorkspaceRepeatCreateChallengeError(
  error: unknown
): error is WorkspaceRepeatCreateChallengeError {
  return error instanceof WorkspaceRepeatCreateChallengeError;
}

export class WorkspaceJobClientRequestError extends Error {
  code: string | null;
  details: unknown;
  retryAfterSeconds: number | null;
  status: number;

  constructor(apiError: ApiErrorPayload) {
    super(apiError.message);
    this.name = 'WorkspaceJobClientRequestError';
    this.code = apiError.code;
    this.details = apiError.details;
    this.retryAfterSeconds = apiError.retryAfterSeconds;
    this.status = apiError.status;
  }
}

export function readWorkspaceTurnFromRetryError(
  error: unknown
): WorkspaceTurn | null {
  if (!(error instanceof WorkspaceJobClientRequestError)) return null;
  if (!error.details || typeof error.details !== 'object') return null;
  const turn = (error.details as { workspaceTurn?: unknown }).workspaceTurn;
  if (!turn || typeof turn !== 'object' || Array.isArray(turn)) return null;
  if (typeof (turn as { id?: unknown }).id !== 'string') return null;
  return turn as WorkspaceTurn;
}

export class WorkspaceCreateJobTransportError extends Error {
  cause: unknown;

  constructor(cause: unknown) {
    super('The job creation response could not be confirmed.');
    this.name = 'WorkspaceCreateJobTransportError';
    this.cause = cause;
  }
}

export function isWorkspaceCreateJobTransportError(
  error: unknown
): error is WorkspaceCreateJobTransportError {
  return error instanceof WorkspaceCreateJobTransportError;
}

function isAmbiguousWorkspaceCreateOutcome(error: unknown) {
  return (
    isWorkspaceCreateJobTransportError(error) ||
    (error instanceof WorkspaceJobClientRequestError &&
      error.status === 503 &&
      (error.code === DATABASE_UNAVAILABLE_ERROR_CODE ||
        error.code === AI_JOB_COMMAND_ERROR_CODES.DISPATCH_UNCONFIRMED))
  );
}

export class WorkspaceJobSubmissionUnconfirmedError extends Error {
  cause: unknown;

  constructor(cause: unknown) {
    super('The job creation outcome could not be confirmed.');
    this.name = 'WorkspaceJobSubmissionUnconfirmedError';
    this.cause = cause;
  }
}

export function isWorkspaceJobSubmissionUnconfirmedError(
  error: unknown
): error is WorkspaceJobSubmissionUnconfirmedError {
  return error instanceof WorkspaceJobSubmissionUnconfirmedError;
}

export class WorkspaceRepeatJobSubmissionUnconfirmedError extends Error {
  cause: unknown;
  recoveredJobs: Array<{
    clientRequestId: string;
    job: ApiAiJob | null;
  }>;

  constructor(
    cause: unknown,
    recoveredJobs: Array<{
      clientRequestId: string;
      job: ApiAiJob | null;
    }>
  ) {
    super('The repeated job creation outcome could not be confirmed.');
    this.name = 'WorkspaceRepeatJobSubmissionUnconfirmedError';
    this.cause = cause;
    this.recoveredJobs = recoveredJobs;
  }
}

export function isWorkspaceRepeatJobSubmissionUnconfirmedError(
  error: unknown
): error is WorkspaceRepeatJobSubmissionUnconfirmedError {
  return error instanceof WorkspaceRepeatJobSubmissionUnconfirmedError;
}

function isRetryableWorkspaceJobStatusReadError(error: unknown) {
  return (
    isTransientNetworkRequestError(error) ||
    (error instanceof WorkspaceJobClientRequestError &&
      isRetryablePureReadResponseStatus(error.status))
  );
}

export class WorkspaceLoginRequiredError extends WorkspaceJobClientRequestError {
  constructor(apiError: ApiErrorPayload) {
    super(apiError);
    this.name = 'WorkspaceLoginRequiredError';
  }
}

export function isWorkspaceLoginRequiredError(
  error: unknown
): error is WorkspaceLoginRequiredError {
  return error instanceof WorkspaceLoginRequiredError;
}

export class WorkspaceTempBlockedError extends WorkspaceJobClientRequestError {
  constructor(apiError: ApiErrorPayload) {
    super(apiError);
    this.name = 'WorkspaceTempBlockedError';
  }
}

export function createWorkspaceJobClientError(apiError: ApiErrorPayload) {
  if (
    apiError.status === 401 &&
    apiError.code === SECURITY_GOOGLE_LOGIN_REQUIRED_CODE
  ) {
    return new WorkspaceLoginRequiredError(apiError);
  }

  if (apiError.status === 429 && apiError.code === SECURITY_TEMP_BLOCKED_CODE) {
    return new WorkspaceTempBlockedError(apiError);
  }

  return new WorkspaceJobClientRequestError(apiError);
}

export function isTerminalWorkspaceJob(job: ApiAiJob) {
  return job.status === 'succeeded' || job.status === 'failed';
}

function isAbortError(error: unknown) {
  return (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.name === 'AbortError')
  );
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const cleanup = () => {
      window.clearTimeout(timer);
      signal?.removeEventListener('abort', handleAbort);
    };

    const handleAbort = () => {
      cleanup();
      reject(new DOMException('Aborted', 'AbortError'));
    };

    if (signal?.aborted) {
      handleAbort();
      return;
    }

    signal?.addEventListener('abort', handleAbort, { once: true });
  });
}

export function hasPendingWorkspaceMediaDerivatives(job: ApiAiJob) {
  if (job.status !== 'succeeded') return false;
  if (job.mediaReadinessByAssetId) {
    return Object.values(job.mediaReadinessByAssetId).some(
      (readiness) => readiness === 'pending'
    );
  }
  return Boolean(
    job.output?.assets?.some(
      (asset) =>
        Boolean(asset.original.key) &&
        requiresMediaAssetDerivativeProcessing(asset) &&
        (!isMediaAssetDerivativeComplete(asset) ||
          !isMediaAssetDerivativePolicyCurrent(asset))
    )
  );
}

export async function settleWorkspaceJobMediaDerivatives(params: {
  fetchJobStatus: (jobId: string) => Promise<ApiAiJob>;
  job: ApiAiJob;
  jobId: string;
  onJobStatus: (job: ApiAiJob) => void;
  signal?: AbortSignal;
  isReady?: (job: ApiAiJob) => boolean;
  maxAttempts?: number;
  resolveDelayMs?: (attempt: number) => number;
}) {
  let current = params.job;
  const isReady =
    params.isReady ??
    ((job: ApiAiJob) => !hasPendingWorkspaceMediaDerivatives(job));
  const maxAttempts =
    params.maxAttempts ?? MEDIA_DERIVATIVE_SETTLE_MAX_ATTEMPTS;
  const resolveDelayMs =
    params.resolveDelayMs ?? resolveMediaDerivativeSettleDelayMs;

  for (
    let attempt = 0;
    attempt < maxAttempts && !isReady(current);
    attempt += 1
  ) {
    await sleep(resolveDelayMs(attempt), params.signal);
    try {
      current = await params.fetchJobStatus(params.jobId);
      params.onJobStatus(current);
    } catch (error) {
      if (
        isAbortError(error) ||
        !isRetryableWorkspaceJobStatusReadError(error)
      ) {
        throw error;
      }
    }
  }
  return current;
}

export async function fetchWorkspaceJobStatus(params: {
  getJobPathPrefix: string;
  jobId: string;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  const response = await fetchPureReadWithOneRetry(
    `${params.getJobPathPrefix}/${params.jobId}`,
    undefined,
    {
      signal: params.signal,
      timeoutMs: GET_JOB_TIMEOUT_MS,
      timeoutMessage: params.timeoutMessage,
    }
  );

  if (!response.ok) {
    throw createWorkspaceJobClientError(await parseApiError(response));
  }

  return (await response.json()) as ApiAiJob;
}

export async function fetchWorkspaceTurnsPage(params: {
  path?: string;
  cursor?: string | null;
  signal?: AbortSignal;
}) {
  const url = new URL(
    params.path ?? '/api/ai/workspace-turns',
    typeof window === 'undefined' ? 'http://localhost' : window.location.origin
  );
  if (params.cursor) url.searchParams.set('cursor', params.cursor);
  const response = await fetch(`${url.pathname}${url.search}`, {
    signal: params.signal,
  });

  if (!response.ok) {
    throw createWorkspaceJobClientError(await parseApiError(response));
  }

  return (await response.json()) as WorkspaceTurnsPage;
}

export async function resolveWorkspaceJobByClientRequestId(params: {
  getJobPathPrefix: string;
  clientRequestId: string;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  const response = await fetchPureReadWithOneRetry(
    `${params.getJobPathPrefix}/resolve/${encodeURIComponent(
      params.clientRequestId
    )}`,
    undefined,
    {
      signal: params.signal,
      timeoutMs: GET_JOB_TIMEOUT_MS,
      timeoutMessage: params.timeoutMessage,
    }
  );

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    throw createWorkspaceJobClientError(await parseApiError(response));
  }

  return (await response.json()) as ApiAiJob;
}

export async function recoverWorkspaceJobByClientRequestId(params: {
  getJobPathPrefix: string;
  clientRequestId: string;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  const recoveryController = new AbortController();
  let recoveryWindowExpired = false;
  let recoveryAttempt = 1;
  const abortFromCaller = () => {
    recoveryController.abort(params.signal?.reason);
  };
  if (params.signal?.aborted) {
    abortFromCaller();
  } else {
    params.signal?.addEventListener('abort', abortFromCaller, { once: true });
  }
  const recoveryTimeoutId = window.setTimeout(() => {
    recoveryWindowExpired = true;
    recoveryController.abort();
  }, CREATE_JOB_RECOVERY_WINDOW_MS);

  try {
    while (true) {
      try {
        const recoveredJob = await resolveWorkspaceJobByClientRequestId({
          ...params,
          signal: recoveryController.signal,
        });
        if (recoveredJob) {
          return recoveredJob;
        }
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }
        if (!isRetryableWorkspaceJobStatusReadError(error)) {
          throw error;
        }
      }

      await sleep(
        resolvePollDelayMs(recoveryAttempt),
        recoveryController.signal
      );
      recoveryAttempt += 1;
    }
  } catch (error) {
    if (params.signal?.aborted && isAbortError(error)) {
      throw error;
    }
    if (recoveryWindowExpired && isAbortError(error)) {
      return null;
    }
    throw error;
  } finally {
    window.clearTimeout(recoveryTimeoutId);
    params.signal?.removeEventListener('abort', abortFromCaller);
  }
}

async function timeoutWorkspaceJob(params: {
  getJobPathPrefix: string;
  jobId: string;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  const response = await fetchCommandOnce(
    `${params.getJobPathPrefix}/${params.jobId}/timeout`,
    {
      method: 'POST',
      signal: params.signal,
    },
    {
      signal: params.signal,
      timeoutMs: TIMEOUT_JOB_TIMEOUT_MS,
      timeoutMessage: params.timeoutMessage,
    }
  );

  if (!response.ok) {
    throw createWorkspaceJobClientError(await parseApiError(response));
  }
}

export async function createWorkspaceJob(params: {
  createJobPath: string;
  payload: WorkspaceCreateJobPayload;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  let response: Response;
  try {
    response = await fetchCommandOnce(
      params.createJobPath,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params.payload),
      },
      {
        signal: params.signal,
        timeoutMs: CREATE_JOB_TIMEOUT_MS,
        timeoutMessage: params.timeoutMessage,
      }
    );
  } catch (error) {
    if (isTransientNetworkRequestError(error)) {
      throw new WorkspaceCreateJobTransportError(error);
    }
    throw error;
  }

  if (!response.ok) {
    const apiError = await parseApiError(response);
    if (
      apiError.status === 403 &&
      apiError.code === SECURITY_CHALLENGE_REQUIRED_CODE
    ) {
      throw new WorkspaceCreateChallengeError(params.payload, apiError);
    }
    throw createWorkspaceJobClientError(apiError);
  }

  return (await response.json()) as ApiAiJob;
}

export async function createWorkspaceRepeatJobs(params: {
  createJobPath: string;
  payload: WorkspaceRepeatCreateJobPayload;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  let response: Response;
  try {
    response = await fetchCommandOnce(
      params.createJobPath,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(params.payload),
      },
      {
        signal: params.signal,
        timeoutMs: CREATE_JOB_TIMEOUT_MS,
        timeoutMessage: params.timeoutMessage,
      }
    );
  } catch (error) {
    if (isTransientNetworkRequestError(error)) {
      throw new WorkspaceCreateJobTransportError(error);
    }
    throw error;
  }

  if (!response.ok) {
    const apiError = await parseApiError(response);
    if (
      apiError.status === 403 &&
      apiError.code === SECURITY_CHALLENGE_REQUIRED_CODE
    ) {
      throw new WorkspaceRepeatCreateChallengeError(params.payload, apiError);
    }
    throw createWorkspaceJobClientError(apiError);
  }

  return (await response.json()) as WorkspaceRepeatCreateJobResult;
}

/**
 * A create command is sent exactly once. If its response is lost, reconcile
 * its durable outcome through bounded actor-owned pure reads instead of
 * replaying the command and risking a second debit or provider task.
 */
export async function createOrResolveWorkspaceJob(params: {
  createJobPath: string;
  getJobPathPrefix: string;
  payload: WorkspaceCreateJobPayload;
  signal?: AbortSignal;
  createTimeoutMessage: string;
  statusTimeoutMessage: string;
}) {
  try {
    return await createWorkspaceJob({
      createJobPath: params.createJobPath,
      payload: params.payload,
      signal: params.signal,
      timeoutMessage: params.createTimeoutMessage,
    });
  } catch (error) {
    if (!isAmbiguousWorkspaceCreateOutcome(error)) {
      throw error;
    }

    const recoveredJob = await recoverWorkspaceJobByClientRequestId({
      getJobPathPrefix: params.getJobPathPrefix,
      clientRequestId: params.payload.clientRequestId,
      signal: params.signal,
      timeoutMessage: params.statusTimeoutMessage,
    });
    if (recoveredJob) {
      return recoveredJob;
    }

    throw new WorkspaceJobSubmissionUnconfirmedError(error);
  }
}

export async function retryWorkspaceJob(params: {
  retryJobPath: string;
  getJobPathPrefix: string;
  sourceJobId: string;
  clientRequestId: string;
  signal?: AbortSignal;
  timeoutMessage: string;
  statusTimeoutMessage: string;
}) {
  const recover = () =>
    recoverWorkspaceJobByClientRequestId({
      getJobPathPrefix: params.getJobPathPrefix,
      clientRequestId: params.clientRequestId,
      signal: params.signal,
      timeoutMessage: params.statusTimeoutMessage,
    });

  try {
    const response = await fetchCommandOnce(
      params.retryJobPath,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ clientRequestId: params.clientRequestId }),
      },
      {
        signal: params.signal,
        timeoutMs: CREATE_JOB_TIMEOUT_MS,
        timeoutMessage: params.timeoutMessage,
      }
    );

    if (!response.ok) {
      const apiError = await parseApiError(response);
      if (
        apiError.status === 503 &&
        (apiError.code === DATABASE_UNAVAILABLE_ERROR_CODE ||
          apiError.code === AI_JOB_COMMAND_ERROR_CODES.DISPATCH_UNCONFIRMED)
      ) {
        const recoveredJob = await recover();
        if (recoveredJob) return recoveredJob;
        throw new WorkspaceJobSubmissionUnconfirmedError(apiError);
      }
      throw createWorkspaceJobClientError(apiError);
    }

    return (await response.json()) as ApiAiJob;
  } catch (error) {
    if (!isTransientNetworkRequestError(error)) {
      throw error;
    }

    const recoveredJob = await recover();
    if (recoveredJob) return recoveredJob;
    throw new WorkspaceJobSubmissionUnconfirmedError(error);
  }
}

/**
 * The repeat command is also sent exactly once. Child request IDs make every
 * created job independently recoverable if the aggregate response is lost.
 */
export async function createOrResolveWorkspaceRepeatJobs(params: {
  createJobPath: string;
  getJobPathPrefix: string;
  payload: WorkspaceRepeatCreateJobPayload;
  signal?: AbortSignal;
  createTimeoutMessage: string;
  statusTimeoutMessage: string;
}) {
  try {
    return await createWorkspaceRepeatJobs({
      createJobPath: params.createJobPath,
      payload: params.payload,
      signal: params.signal,
      timeoutMessage: params.createTimeoutMessage,
    });
  } catch (error) {
    if (!isAmbiguousWorkspaceCreateOutcome(error)) {
      throw error;
    }

    const recoveredJobs = await Promise.all(
      params.payload.clientRequestIds.map(async (clientRequestId) => ({
        clientRequestId,
        job: await recoverWorkspaceJobByClientRequestId({
          getJobPathPrefix: params.getJobPathPrefix,
          clientRequestId,
          signal: params.signal,
          timeoutMessage: params.statusTimeoutMessage,
        }),
      }))
    );
    if (recoveredJobs.every((item) => item.job)) {
      return {
        clientRequestId: params.payload.clientRequestId,
        items: recoveredJobs.map((item) => ({
          clientRequestId: item.clientRequestId,
          job: item.job as ApiAiJob,
        })),
      } satisfies WorkspaceRepeatCreateJobResult;
    }

    throw new WorkspaceRepeatJobSubmissionUnconfirmedError(
      error,
      recoveredJobs
    );
  }
}

export async function pollWorkspaceJob(params: {
  fetchJobStatus: (jobId: string) => Promise<ApiAiJob>;
  getJobPathPrefix: string;
  jobId: string;
  onJobStatus: (job: ApiAiJob) => void;
  onStatusReadStateChange?: (state: 'available' | 'unavailable') => void;
  signal?: AbortSignal;
  timeoutMessage: string;
}) {
  let confirmedPollAttempt = 1;
  let statusReadFailureAttempt = 0;
  let statusReadUnavailable = false;

  const markStatusReadUnavailable = () => {
    if (!statusReadUnavailable) {
      statusReadUnavailable = true;
      params.onStatusReadStateChange?.('unavailable');
    }
  };

  const readKnownJobStatus = async () => {
    while (true) {
      try {
        const nextJob = await params.fetchJobStatus(params.jobId);
        statusReadFailureAttempt = 0;
        if (statusReadUnavailable) {
          statusReadUnavailable = false;
          params.onStatusReadStateChange?.('available');
        }
        return nextJob;
      } catch (error) {
        if (isAbortError(error)) {
          throw error;
        }

        if (!isRetryableWorkspaceJobStatusReadError(error)) {
          throw error;
        }

        markStatusReadUnavailable();
        statusReadFailureAttempt += 1;
        await sleep(
          resolvePollDelayMs(statusReadFailureAttempt),
          params.signal
        );
      }
    }
  };

  const requestTimeoutAndReconcile = async () => {
    try {
      await timeoutWorkspaceJob({
        getJobPathPrefix: params.getJobPathPrefix,
        jobId: params.jobId,
        signal: params.signal,
        timeoutMessage: params.timeoutMessage,
      });
    } catch (error) {
      if (isAbortError(error)) {
        throw error;
      }

      if (!isRetryableWorkspaceJobStatusReadError(error)) {
        throw error;
      }

      // The timeout command is never replayed after an unknown write outcome.
      // Reconcile the known job through the pure-read path instead.
      markStatusReadUnavailable();
    }

    return readKnownJobStatus();
  };

  while (true) {
    let nextJob = await readKnownJobStatus();
    let reconciledForTimeout = false;
    if (shouldTimeoutEnhancerJobOnClient(nextJob)) {
      nextJob = await requestTimeoutAndReconcile();
      reconciledForTimeout = true;
    }

    params.onJobStatus(nextJob);

    if (isTerminalWorkspaceJob(nextJob)) {
      return nextJob;
    }

    if (!shouldContinueEnhancerJobPolling(nextJob)) {
      return nextJob;
    }

    if (!reconciledForTimeout && confirmedPollAttempt >= MAX_POLL_ATTEMPTS) {
      const reconciledJob = await requestTimeoutAndReconcile();
      params.onJobStatus(reconciledJob);
      if (
        isTerminalWorkspaceJob(reconciledJob) ||
        !shouldContinueEnhancerJobPolling(reconciledJob)
      ) {
        return reconciledJob;
      }
      confirmedPollAttempt = 1;
      continue;
    }

    if (reconciledForTimeout) {
      confirmedPollAttempt = 1;
    }
    await sleep(resolvePollDelayMs(confirmedPollAttempt), params.signal);
    confirmedPollAttempt += 1;
  }
}
